import React from "react";
import { ArrowRight, Check, Inbox } from "../icons";

/**
 * Shared UI primitives.
 *
 * These are thin wrappers over the design-system classes — their job is to make
 * the *vocabulary* explicit (a status is a status, a ring is a ring) so screens
 * read as composition rather than as styling. Any visual decision lives in
 * design-system.css; nothing here hard-codes a colour.
 */

/* ------------------------------------------------------------------ Card */

export const Card = ({
  as: Tag = "div",
  pad = false,
  raised = false,
  interactive = false,
  accent,
  className = "",
  children,
  ...rest
}) => (
  <Tag
    className={[
      "ds-card",
      pad && "ds-card--pad",
      raised && "ds-card--raised",
      interactive && "ds-card--interactive",
      accent && "ds-card--accent",
      accent === "warn" && "ds-card--accent-warn",
      accent === "success" && "ds-card--accent-success",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    {...rest}
  >
    {children}
  </Tag>
);

/* --------------------------------------------------------------- Buttons */

export const Button = ({
  variant = "primary",
  size,
  block = false,
  icon: Icon,
  iconRight: IconRight,
  className = "",
  children,
  ...rest
}) => (
  <button
    type="button"
    className={[
      "ds-btn",
      `ds-btn--${variant}`,
      size === "sm" && "ds-btn--sm",
      block && "ds-btn--block",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    {...rest}
  >
    {Icon && <Icon size={size === "sm" ? 14 : 15} />}
    {children}
    {IconRight && <IconRight size={size === "sm" ? 14 : 15} />}
  </button>
);

export const TextLink = ({ children, icon: Icon = ArrowRight, ...rest }) => (
  <button type="button" className="ds-link" {...rest}>
    {children}
    {Icon && <Icon size={14} />}
  </button>
);

/* ------------------------------------------------------- Chips & badges */

/** Uppercase type marker — SCORM, MODULE, LESSON, WARM UP. */
export const Chip = ({ tone = "brand", children }) => (
  <span className={`ds-chip${tone === "brand" ? "" : ` ds-chip--${tone}`}`}>
    {children}
  </span>
);

const STATUS_META = {
  assigned: { label: "Not started", tone: "assigned" },
  "in-progress": { label: "In progress", tone: "in-progress" },
  completed: { label: "Completed", tone: "completed" },
};

/**
 * Status pill driven by the SERVER's status value, so the label a learner sees
 * always matches what the backend decided (utils/completion.ts). `percent` is
 * appended only while genuinely partial.
 */
export const StatusPill = ({ status, percent, overdue = false }) => {
  const meta = STATUS_META[status] || STATUS_META.assigned;
  const tone = overdue && status !== "completed" ? "overdue" : meta.tone;
  const label = overdue && status !== "completed" ? "Overdue" : meta.label;
  const suffix =
    percent > 0 && percent < 100 && status !== "completed" ? ` · ${percent}%` : "";
  return (
    <span className={`ds-status ds-status--${tone}`}>
      {status === "completed" && <Check size={12} />}
      {label}
      {suffix}
    </span>
  );
};

/* ------------------------------------------------------------- Icon tile */

export const IconTile = ({ icon: Icon, brand = false, large = false, size }) => (
  <span
    className={[
      "ds-tile",
      brand && "ds-tile--brand",
      large && "ds-tile--lg",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <Icon size={size || (large ? 21 : 18)} />
  </span>
);

/* -------------------------------------------------------------- Progress */

export const ProgressBar = ({ value = 0, tone, onBrand = false }) => (
  <span className={`ds-bar${onBrand ? " ds-bar--onbrand" : ""}`}>
    <span
      className={`ds-bar__fill${tone ? ` ds-bar__fill--${tone}` : ""}`}
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </span>
);

export const ProgressRow = ({ value = 0, tone, label }) => (
  <span className="ds-bar__row">
    <ProgressBar value={value} tone={tone} />
    <span className="ds-bar__value">{label ?? `${Math.round(value)}%`}</span>
  </span>
);

/**
 * Percentage ring. An SVG arc rather than a canvas chart so it stays crisp at
 * any size and costs nothing to render in a list.
 */
export const ProgressRing = ({
  value = 0,
  size = 46,
  thickness = 4,
  tone = "brand",
  label,
  labelSize,
}) => {
  const pct = Math.max(0, Math.min(100, value));
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const stroke =
    tone === "success"
      ? "var(--success)"
      : tone === "warn"
      ? "var(--warn)"
      : "var(--brand-600)";

  return (
    <span className="ds-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--border)"
          strokeWidth={thickness}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset .45s ease" }}
        />
      </svg>
      <span
        className="ds-ring__label"
        style={{ fontSize: labelSize || Math.max(10, size * 0.26), color: stroke }}
      >
        {label ?? `${Math.round(pct)}%`}
      </span>
    </span>
  );
};

/**
 * Donut with an arbitrary number of slices. Used for the "overall progress"
 * breakdown, where the three states must add up to the whole.
 */
export const Donut = ({ slices = [], size = 132, thickness = 15, centre }) => {
  const total = slices.reduce((sum, s) => sum + (s.value || 0), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <span className="ds-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,.18)"
          strokeWidth={thickness}
        />
        {total > 0 &&
          slices.map((s, i) => {
            const len = (c * (s.value || 0)) / total;
            const dash = `${len} ${c - len}`;
            const node = (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += len;
            return node;
          })}
      </svg>
      {centre && <span className="ds-ring__label">{centre}</span>}
    </span>
  );
};

/* ------------------------------------------------------- Section heading */

export const SectionHeading = ({ title, description, action, className = "" }) => (
  <div className={`ds-section__head ${className}`}>
    <div>
      <h2 className="ds-h2">{title}</h2>
      {description && <p className="ds-sub">{description}</p>}
    </div>
    {action}
  </div>
);

/* ---------------------------------------------------- Segmented control */

export const Segmented = ({ options, value, onChange, onBrand = false }) => (
  <div className={`ds-segmented${onBrand ? " ds-segmented--onbrand" : ""}`}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        className="ds-segmented__item"
        aria-pressed={value === o.value}
        onClick={(e) => {
          e.stopPropagation();
          onChange(o.value);
        }}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/* ----------------------------------------------------------- Empty state */

export const EmptyState = ({ icon: Icon = Inbox, text, action }) => (
  <div className="ds-empty">
    <span className="ds-empty__icon">
      <Icon size={20} />
    </span>
    <p className="ds-empty__text">{text}</p>
    {action}
  </div>
);
